//Modules
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tokenService from '../../utils/tokenService';
import companySettingApi from '../../services/companySettingApi';
import connectApi from '../../services/connectApi';

export const fetchCompanySettings = createAsyncThunk(
  'company/fetchCompanySettings',
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await companySettingApi.list(companyId);
      const settings = res.data.data || [];
      const map = {};
      settings.forEach((s) => {
        map[s.code] = s;
      });
      return map;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to load company settings'
      );
    }
  }
);

// ConnectController answers its failures with a bare JSON string body —
// `UnprocessableEntity("User role / permission required")`,
// `NotFound("Company not found")` — not the `{ message }` envelope the rest
// of this API wraps errors in, so `error.response.data` is itself the
// message on this controller. Same idiom connect-tab.js's apiMessage and
// notification.js's extractErrorMessage already use for the same reason.
const connectApiMessage = (error) => {
  const data = error?.response?.data;
  return (
    data?.Message ||
    data?.message ||
    (typeof data === 'string' ? data : '') ||
    'Failed to load Connect status'
  );
};

// The Settings -> Payments nav entry asks one question — does this company
// have a Stripe Connect account — and asking it per navigation would cost a
// request per click. It is read once per company, beside fetchCompanySettings,
// and served from the store afterwards. Failure leaves it null: fail-closed, so
// the entry stays hidden rather than appearing for a company that has none.
export const fetchConnectStatus = createAsyncThunk(
  'company/fetchConnectStatus',
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await connectApi.getStatus(companyId);
      return connectApi.toConnectStatus(res);
    } catch (error) {
      return rejectWithValue(connectApiMessage(error));
    }
  }
);

const initialState = {
  list: [],
  selected: {},
  settings: {},
  connectStatus: null,
};

export const companySlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCompany: (state, { payload }) => {
      state.list = payload;
    },
    setGlobalCompany: (state, { payload }) => {
      state.selected = payload;
      tokenService.setComp(JSON.stringify(payload));
    },
    setCompanySettings: (state, { payload }) => {
      state.settings = payload;
    },
    clearCompanySettings: (state) => {
      state.settings = {};
      // Connect status is company-scoped too. Defensive only: nothing
      // dispatches this reducer today (the live guarantee against a stale
      // entry is company-changer.js's navigate(0) full reload), but anything
      // that clears the settings must clear this beside them.
      state.connectStatus = null;
    },
    // The Finance section's own status read — the one it re-runs when
    // onboarding changes in place — lands here too, so the sidebar and the
    // Pending band follow without a second request (spec 2026-09-25 §5.6).
    // Same stale-company guard as fetchConnectStatus.fulfilled below.
    setConnectStatus: (state, { payload }) => {
      if (payload?.companyId === state.selected?.id) {
        state.connectStatus = payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanySettings.fulfilled, (state, { payload }) => {
        state.settings = payload;
      })
      // While a request is in flight, the answer on screen is for neither the
      // old company nor the new one for certain -- clearing to null keeps the
      // nav entry fail-closed instead of showing the previous company's
      // account for however long the new company's request takes.
      .addCase(fetchConnectStatus.pending, (state) => {
        state.connectStatus = null;
      })
      // meta.arg is the companyId the thunk was dispatched with. Comparing it
      // to the currently-selected company (rather than writing unconditionally)
      // stops a late response for a company the user has already switched away
      // from from overwriting the newer company's state. Both sides are the
      // numeric/string id as returned by the API and stored verbatim by
      // setGlobalCompany, so === is the same comparison company.js already
      // makes between selected.id and an upsert response's id.
      .addCase(fetchConnectStatus.fulfilled, (state, { meta, payload }) => {
        if (meta?.arg === state.selected?.id) {
          state.connectStatus = payload;
        }
      })
      .addCase(fetchConnectStatus.rejected, (state, { meta }) => {
        // Same out-of-order guard as fulfilled: a stale company's failure
        // must not clobber a newer company's already-resolved status.
        if (meta?.arg === state.selected?.id) {
          state.connectStatus = null;
        }
      });
  },
});

export const {
  setCompany,
  setGlobalCompany,
  setCompanySettings,
  clearCompanySettings,
  setConnectStatus,
} = companySlice.actions;

export default companySlice.reducer;
